// src/lib/queryClient.ts
// Central React Query configuration.
//
// Defaults chosen for Callevo:
//  staleTime   30 s  — fresh enough for dashboard / invite lists that don't change every second
//  gcTime       5 min — keep cached responses in memory so navigation feels instant
//  retry        1     — re-try once on network hiccup; avoid hammering on 4xx errors
//  retryDelay   exponential with 1 s base, cap at 10 s
//  refetchOnWindowFocus false — users switch tabs a lot; don't flood the backend on focus

import { QueryClient } from "@tanstack/react-query";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,          // 30 s
      gcTime: 5 * 60_000,         // 5 min
      retry: 1,
      retryDelay: (attempt) => Math.min(1_000 * 2 ** attempt, 10_000),
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,                   // mutations: never auto-retry (user intent)
    },
  },
});

// ---------------------------------------------------------------------------
// Query key factory — centralised to avoid typo-driven cache misses.
// Usage:  queryKeys.invite.list() → ["invites"]
//         queryKeys.invite.detail("abc") → ["invites", "abc"]
// ---------------------------------------------------------------------------
export const queryKeys = {
  invites: {
    all: () => ["invites"] as const,
    list: () => ["invites", "list"] as const,
    detail: (id: string) => ["invites", id] as const,
    calls: (id: string) => ["invites", id, "calls"] as const,
  },
  tokens: {
    balance: () => ["tokens", "balance"] as const,
  },
  calls: {
    all: () => ["calls"] as const,
    recent: (userId: string) => ["calls", "recent", userId] as const,
    detail: (id: string) => ["calls", id] as const,
  },
  assessment: {
    notes: (callId: string) => ["assessment", "calls", callId, "notes"] as const,
    comparison: (rawIds: string) => ["assessment", "comparison", rawIds] as const,
  },
  settings: {
    scoringWeights: () => ["settings", "scoring-weights"] as const,
  },
  auditLog: {
    list: (page: number) => ["audit-log", page] as const,
  },
  personas: {
    list: () => ["personas", "list"] as const,
  },
};
